import JsBarcode from "jsbarcode";
import { store } from "../redux/store";
import { OrderResult } from "../redux/types/sellScreenTypes";
import CommonService from "../shared/services/commonService";

export interface BarcodeResult {
    svg: string;
    html: string;
    totalWidth: number;
    height: number;
    bars: { x: number; width: number }[];
}

/**
 * Generates barcode data (SVG, HTML container, dimensions, and bar coordinates) for the given value.
 * Supports CODE128 and EAN13 formats.
 */
export function generateBarcodeDetails(
    value: string,
    height: number = 160,
    moduleWidth: number = 3,
    format: string = 'CODE128'
): BarcodeResult | null {
    if (!value || value === '#N/A') return null;

    // Clean value: remove leading '#' for broad barcode scanner compatibility
    const barcodeValue = String(value).replace(/^#/, '').trim();
    if (!barcodeValue) return null;

    try {
        const obj: any = {};
        JsBarcode(obj, barcodeValue, {
            format: format || 'CODE128',
            width: moduleWidth,
            height: height,
            displayValue: false
        });

        const encodings = obj.encodings;
        if (!encodings || encodings.length === 0) return null;

        const binary = encodings.map((e: any) => e.data).join('');
        if (!binary) return null;

        const quietZone = 35; // Generous quiet zone for optical scanner acquisition
        const totalWidth = binary.length * moduleWidth + (quietZone * 2);
        const bwr = 0.5; // Bar Width Reduction in px to counteract thermal dot gain / heat bleed

        let rects = '';
        const bars: { x: number; width: number }[] = [];
        let barWidth = 0;
        for (let b = 0; b < binary.length; b++) {
            const x = quietZone + b * moduleWidth;
            if (binary[b] === '1') {
                barWidth++;
            } else if (barWidth > 0) {
                const rawW = moduleWidth * barWidth;
                const w = Math.max(0.8, rawW - bwr);
                const rx = (x - rawW) + (bwr / 2);
                const barX = Number(rx.toFixed(2));
                const barW = Number(w.toFixed(2));
                bars.push({ x: barX, width: barW });
                rects += `<rect x="${barX}" y="0" width="${barW}" height="${height}"/>`;
                barWidth = 0;
            }
        }
        if (barWidth > 0) {
            const rawW = moduleWidth * barWidth;
            const w = Math.max(0.8, rawW - bwr);
            const rx = (quietZone + binary.length * moduleWidth - rawW) + (bwr / 2);
            const barX = Number(rx.toFixed(2));
            const barW = Number(w.toFixed(2));
            bars.push({ x: barX, width: barW });
            rects += `<rect x="${barX}" y="0" width="${barW}" height="${height}"/>`;
        }

        const svg = `<svg width="${totalWidth}" height="${height}" viewBox="0 0 ${totalWidth} ${height}" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges" style="max-width: 95%; height: auto; display: block; margin: 0 auto; background: #ffffff;">
            <rect width="100%" height="100%" fill="#ffffff"/>
            <g fill="#000000">
                ${rects}
            </g>
        </svg>`;

        const html = `
        <div class="barcodeContainer" style="text-align: center; margin: 25px auto 15px auto; background: #ffffff; padding: 10px 0;">
            ${svg}
            <p class="contentFont" style="font-size: 27px; font-weight: 800; text-align: center; margin: 10px 0 0 0; letter-spacing: 3px;">${value}</p>
        </div>`;

        return {
            svg,
            html,
            totalWidth,
            height,
            bars
        };
    } catch (err) {
        console.error("Failed to generate barcode:", err);
        return null;
    }
}

/**
 * Generates an inline SVG barcode string for the given value (CODE128 format by default).
 * Renders sharp vector bars compatible with thermal receipt printers.
 */
export function generateBarcodeSvg(value: string, height: number = 160, moduleWidth: number = 3, format: string = 'CODE128'): string {
    const details = generateBarcodeDetails(value, height, moduleWidth, format);
    if (details) {
        return details.html;
    }
    return `<p class="contentFont" style="font-size: 28px; font-weight: 800; text-align: center; margin-top: 10px;">* ${value} *</p>`;
}

/**
* Generates dynamic thermal printer receipt HTML from POS order data.
* @param {Object} orderData - POS order object or API response { result: { ... } }
* @returns {string} Fully populated HTML string ready for printing
*/
/**
 * Receipt text comes from customers, staff and store settings. Escape it so a
 * name containing "<" or "&" prints as typed instead of breaking (or injecting
 * into) the receipt markup.
 */
function escapeHtml(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function generateReceiptHtml(orderData: OrderResult) {
    if (!orderData) return "";
    const reduxState = store.getState();
    const storeDetails = reduxState.common.currentUser.value?.store;
    const currentUser = reduxState.common.currentUser.value;
    const currencySymbol = currentUser?.purchasedCurrency?.currencySymbol ?? '₹';
    const locationId = orderData?.locationId || reduxState.common?.currentLocation?.id;
    const newSellscreenState = reduxState?.NewSellscreenSlice;

    const cartProducts = newSellscreenState?.cartProducts?.[locationId];
    const summary = newSellscreenState?.summary?.[locationId];
    const additionalChargeState = newSellscreenState?.additionalCharge?.[locationId];
    const tipState = newSellscreenState?.tip?.[locationId];
    const cartQuantityState = newSellscreenState?.cartQuantity?.[locationId];
    const customerDetailsState = newSellscreenState?.customerDetails;

    // 1. Unify order data (supports both raw order object and API response { result: order })
    const order = (orderData as any)?.result || orderData;

    // 2. Formatting Helpers
    const formatCurrency = (val: any) => {
        const num = parseFloat(val);
        if (isNaN(num)) return CommonService.currencyStandard(currencySymbol, "0");
        return CommonService.currencyStandard(currencySymbol, String(num));
    };

    const formatDate = (dateStr: string) => {
        try {
            const d = dateStr ? new Date(dateStr) : new Date();
            if (isNaN(d.getTime())) return { date: "Thursday, 19 September", time: "6:15 pm", fullTime: "" };

            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
            const monthsShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

            const dayName = days[d.getDay()];
            const dayNameShort = dayName.slice(0, 3);
            const day = d.getDate();
            const monthName = months[d.getMonth()];
            const monthShort = monthsShort[d.getMonth()];

            let hours = d.getHours();
            const minutes = d.getMinutes();
            const ampm = hours >= 12 ? "pm" : "am";
            hours = hours % 12 || 12;
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

            return {
                date: `${dayName}, ${day} ${monthName}`,
                time: `${hours}:${formattedMinutes} ${ampm}`,
                fullTime: `${dayNameShort} ${day},${monthShort.toLowerCase()} ${hours}.${formattedMinutes}${ampm}`
            };
        } catch {
            return { date: "Thursday, 19 September", time: "6:15 pm", fullTime: "" };
        }
    };

    // 3. Extract Order Variables & Details
    const { date: orderDate, time: orderTime, fullTime } = formatDate(order?.createdAt);

    // Order ID display: KN-3768-STORE or #3768
    const orderNo = order.serialOrderId
        ? `${order.orderIdPrefixText ? order.orderIdPrefixText + '-' : ''}${order.serialOrderId}${order?.orderIdSuffixText ? '-' + order.orderIdSuffixText : ''}`
        : order.id ? `#${order.id}` : '#N/A';

    // Generate barcode SVG for the order number
    const barcodeSvgHtml = generateBarcodeSvg(orderNo);

    // Store Details
    const storeName = storeDetails?.displayName || "ZenBasket POS";
    const storeAddress = "12A East police station road, chennai, 628009";
    const storePhone = (order as any).storePhone || (order as any).storeDetails?.phoneNumber || "7897865456";
    const storeEmail = (order as any).storeEmail || (order as any).storeDetails?.email || "zenbasket@mailinator.com";
    const gstin = order.gstin || order.taxTransactionCode || (order as any).storeDetails?.gstin || "567G5DFB79B9";

    // Cashier & Customer Details with Fallbacks
    const cashierName = (order as any).cashierName || (order as any).staffName || (order as any).staff?.name || currentUser?.firstName || currentUser?.name || (order.staffId ? "Staff" : "Cashier");
    const customer = (order as any).customerDetails || customerDetailsState;
    const customerName = (order as any).customerName || customer?.customerName || customer?.name || customer?.firstName || "Walk-in Customer";

    // Format customer address & phone safely
    const customerAddressParts = [
        order.deliveryAddress?.address1 || order.billingAddress?.address1 || customer?.address || customer?.address1,
        order.deliveryAddress?.city || order.billingAddress?.city || customer?.city,
        order.deliveryAddress?.pincode || order.billingAddress?.pincode || customer?.pincode || customer?.postalCode,
        order.phoneNumber || customer?.phoneNumber || customer?.contactNumber
    ].filter(Boolean);
    const customerInfoText = customerAddressParts.length > 0 ? customerAddressParts.join(", ") : (order.phoneNumber || "N/A");

    // 4. Extract Items List from order or NewSellscreenSlice cartProducts
    const items = ((order as any).items && (order as any).items.length > 0)
        ? (order as any).items
        : (order.orderedProductsList && order.orderedProductsList.length > 0)
            ? order.orderedProductsList
            : (cartProducts && cartProducts.length > 0)
                ? cartProducts
                : ((order as any).cartProducts || (order as any).products || []);

    const totalItemsCount = cartProducts?.length ?? items.length;
    const totalQtyCount = cartQuantityState ?? items.reduce((sum: number, item: any) => sum + (Number(item.quantity || item.qty) || 1), 0);

    // 5. Charges and Calculations from NewSellscreenSlice summary / additionalCharge / tip or order
    const subTotal = Number(summary?.subTotal ?? order.subTotal ?? order.orderAmount ?? 0);
    const otherDiscount = Number(summary?.otherDiscount ?? order.otherDiscount ?? 0);
    const couponDiscount = Number(summary?.couponDiscount ?? order.couponDiscount ?? 0);
    const additionalDiscount = Number(summary?.additionalDiscount ?? order.additionalDiscount ?? order.posAdditionalDiscount ?? 0);
    const discountAmount = Number(summary?.discountedPrice ?? order.discountAmount ?? order.orderDiscount ?? 0);
    const extraAmount = Number(order.extraAmount ?? order.addOnPrice ?? 0);
    const taxAmount = Number(summary?.tax ?? order.taxAmount ?? order.inclusiveTaxAmount ?? 0);
    const shippingAmount = Number(order.shippingRate ?? order.shippingAmount ?? 0);
    const tipAmount = Number(typeof tipState === 'number' ? tipState : (order.tipAmount ?? 0));
    const additionalCharge = Number(additionalChargeState?.amount ?? order.additionalCharge ?? order.serviceCharge ?? 0);
    // Sale carts: the rounded amount collected when the store rounds off sales
    const grandTotal = Number(summary?.payableTotal ?? summary?.grandTotal ?? order.totalAmount ?? order.orderAmount ?? 0);
    const amountPaid = Number(order.amountPaid ?? grandTotal);
    const earningPoints = Number(order.earningPoints ?? 0);
    const cashRounding = Number(summary?.roundOff ?? order.cashRounding ?? order.roundOffAmount ?? 0);
    const totalSavings = otherDiscount + couponDiscount + additionalDiscount + discountAmount;

    // Payment Transaction Details
    const transactions = (order as any).paymentDetails?.transactionData || (order as any).transactions || [];

    // Base64 Assets
    const companyLogoBase64 = "data:image/png;base64,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";

    return `<!DOCTYPE html>
<html lang="en">

<head>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Receipt</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background-color: white;
            color: #000;
        }

        .container {
            flex: 1;
            background-color: white;
            padding: 24px;
            box-sizing: border-box;
        }

        .header {
            text-align: center;
            margin-bottom: 10px;
        }

        .companyDetails {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
        }

        .companyLogo {
            width: 168px;
            height: 116px;
            margin-right: 20px;
            filter: contrast(200%) brightness(120%);
        }

        .detailsText {
            flex-direction: column;
            align-items: center;
        }

        .footer {
            text-align: center;
            margin-top: 10px;
        }

        .socialIcons {
            margin-top: 10px;
            text-align: center;
        }

        .socialIcon {
            margin-right: 4px;
            width: 63px;
            height: 63px;
        }

        .contentFont {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 36px;
            margin: 0px;
            margin-top: 3px;
        }

        .flexStyle {
            display: flex;
            align-items: center;
            margin: 0px;
        }

        tr.border-bottom td {
            border-bottom: 0.5pt dashed #000;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 32px;
            font-weight: 750;
            padding-bottom: 10px;
        }

        hr {
            display: block;
            height: 3px;
            background: transparent;
            width: 100%;
            border: none;
            border-top: solid 1px #000;
            font-weight: 900;
        }

        /* Thermal printer: font, icons and images */
        @media print {
            body {
                font-size: 29px !important;
            }

            .contentFont {
                font-size: 29px !important;
                line-height: 1.25 !important;
            }

            tr.border-bottom td,
            th,
            td {
                font-size: 29px !important;
            }

            .header h2 {
                font-size: 46px !important;
            }

            .companyLogo {
                width: 168px !important;
                height: 116px !important;
            }

            .socialIcon {
                width: 63px !important;
                height: 63px !important;
            }

            .barcodeContainer svg {
                width: 95% !important;
                max-width: 620px !important;
                height: auto !important;
            }
        }
    </style>
</head>

<body>
    <div class="container">
        <div class="header">
            <h2 style="display: inline; font-size: 46px; margin: 0px; font-family: Arial, Helvetica, sans-serif; font-weight: 800;">Order Receipt</h2>
        </div>

        <!-- Store & Order Date Header -->
        <div class="companyDetails" style="justify-content: space-between;">
            <div class="companyDetails">
                <img src="${companyLogoBase64}" alt="Logo" class="companyLogo">
                <div class="detailsText">
                    <p class="contentFont" style="font-weight: 800;">${escapeHtml(storeName)}</p>
                    <p class="contentFont" style="font-size: 32px; font-weight: 750;">${escapeHtml(storeAddress)}</p>
                </div>
            </div>
            <div>
                <p class="contentFont" style="text-align: right; font-size: 32px; font-weight: 750;">${orderDate}</p>
                <p class="contentFont" style="text-align: right; font-size: 32px; font-weight: 875000;">${orderTime}</p>
            </div>
        </div>

        <hr style="border-top: 1px dashed #000;">

        <!-- Order & GSTIN -->
        <div class="companyDetails" style="justify-content: space-between;">
            <div class="flexStyle">
                <p class="contentFont" style="margin-right: 4px; margin-top:0;">Order No:</p>
                <p class="contentFont" style="font-weight: 900; margin-top:0;">${escapeHtml(orderNo)}</p>
            </div>
            <div class="flexStyle">
                <p class="contentFont" style="margin-right: 4px; margin-top:0;">GSTIN:</p>
                <p class="contentFont" style="font-weight: 800; margin-top:0;">${escapeHtml(gstin)}</p>
            </div>
        </div>

        <!-- Dynamic Product Table -->
        <table style="text-align: center; width: 100%;">
            <thead style="border: 5px;">
                <tr class="contentFont">
                    <th>S.No</th>
                    <th>Products</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Tax</th>
                    <th>Total</th>
                </tr>
            </thead>
            <hr style="border-top: 1px dashed #000;">
            ${items.length > 0 ? items.map((item: any, index: number) => {
        const name = item.variantName
            ? `${item.name || item.productName || 'Product'} - ${item.variantName}`
            : (item.name || item.productName || item.combinationName || item.title || 'Product');
        const qty = item.quantity || item.qty || 1;
        const price = Number(item.price || item.unitPrice || 0);
        const tax = Number(item.tax || item.taxAmount || 0);
        const total = Number(item.finalPrice ?? item.total ?? item.totalPrice ?? (price * qty + tax));

        return `
                <tbody>
                    <tr class="border-bottom">
                        <td>${index + 1}</td>
                        <td>${escapeHtml(name)}</td>
                        <td>${qty}</td>
                        <td>${formatCurrency(price)}</td>
                        <td>${formatCurrency(tax)}</td>
                        <td>${formatCurrency(total)}</td>
                    </tr>
                </tbody>`;
    }).join('') : `
                <tbody>
                    <tr class="border-bottom">
                        <td colspan="6" style="padding: 10px;">No items</td>
                    </tr>
                </tbody>`}
        </table>

        <!-- Subtotal (Always Shown) -->
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">SubTotal</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(subTotal)}</p>
        </div>

        <!-- Conditional Charges & Discounts -->
        ${otherDiscount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Other Discount</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">-${formatCurrency(otherDiscount)}</p>
        </div>` : ''}

        ${couponDiscount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">coupon Discount</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">-${formatCurrency(couponDiscount)}</p>
        </div>` : ''}

        ${additionalDiscount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Additional Discount</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">-${formatCurrency(additionalDiscount)}</p>
        </div>` : ''}

        ${discountAmount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">discounted Price</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">-${formatCurrency(discountAmount)}</p>
        </div>` : ''}

        ${extraAmount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Add on price</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(extraAmount)}</p>
        </div>` : ''}

        ${taxAmount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Tax</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(taxAmount)}</p>
        </div>` : ''}

        ${shippingAmount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">shipping</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(shippingAmount)}</p>
        </div>` : ''}

        ${tipAmount > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Tip</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(tipAmount)}</p>
        </div>` : ''}

        ${additionalCharge > 0 ? `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 6px;">
            <p class="contentFont">Additional charge</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(additionalCharge)}</p>
        </div>` : ''}

        <hr style="border-top: 1px dashed #000;">

        <!-- Grand Total -->
        <div class="flexStyle" style="justify-content: space-between;">
            <div class="flexStyle">
                <p class="contentFont" style="font-size: 36px; margin-top: 0px; font-weight: 900;">Grand Total</p>
                <p class="contentFont" style="font-size: 32px; margin-top: 0px; margin-left: 4px; font-weight: 900;">
                    (Item:${totalItemsCount}, qty:${totalQtyCount})</p>
            </div>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(grandTotal)}</p>
        </div>

        <hr style="border-top: 1px dashed #000;">

        <!-- Cash Rounding (+ or -, only when rounded) -->
        ${cashRounding !== 0 ? `
        <div class="flexStyle" style="justify-content: space-between;">
            <div>
                <p class="contentFont" style="margin-top: 0px;">Cash Rounding</p>
                <p class="contentFont" style="font-size: 32px; font-weight: 800;">${fullTime}</p>
            </div>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${cashRounding > 0 ? "+" : "-"} ${formatCurrency(Math.abs(cashRounding))}</p>
        </div>
        ` : ''}

        <!-- Payment Modes / Cash Transactions -->
        ${transactions.length > 0 ? transactions.map((t: any) => `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 10px;">
            <div>
                <p class="contentFont" style="margin-top: 0px;">${escapeHtml(t.paymentMode || t._paymentMethod || "Paid")}</p>
                <p class="contentFont" style="font-size: 32px; font-weight: 800;">${fullTime}</p>
            </div>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(t.amount || t._paidAmount || 0)}</p>
        </div>`).join('') : `
        <div class="flexStyle" style="justify-content: space-between; margin-top: 10px;">
            <div>
                <p class="contentFont" style="margin-top: 0px;">Amount Paid</p>
                <p class="contentFont" style="font-size: 32px; font-weight: 800;">${fullTime}</p>
            </div>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(amountPaid)}</p>
        </div>`}

        <!-- Total Savings (if > 0) -->
        ${totalSavings > 0 ? `
        <hr style="border-top: 1px dashed #000;">
        <div class="flexStyle" style="justify-content: space-between;">
            <p class="contentFont" style="font-size: 36px; margin-top: 0px;">Total Savings</p>
            <p class="contentFont" style="font-weight: 900; margin-top:0;">${formatCurrency(totalSavings)}</p>
        </div>` : ''}

        <!-- Loyalty Points (if > 0) -->
        ${earningPoints > 0 ? `
        <hr style="border-top: 1px dashed #000;">
        <div class="flexStyle" style="justify-content: center;">
            <p class="contentFont" style="margin-top: 0px;">You will earn ${earningPoints} loyalty/store credit on this order</p>
        </div>` : ''}

        <hr style="border-top: 1px dashed #000;">

        <!-- Cashier & Customer Details -->
        <div class="flexStyle" style="justify-content: space-between;">
            <div>
                <p class="contentFont" style="margin-top: 0px; font-size: 32px;">Sold by</p>
                <p class="contentFont" style="font-weight: 900;">${escapeHtml(cashierName)}</p>
            </div>
            <div>
                <p class="contentFont" style="margin-top: 0px; font-weight: 900; text-align: right;">${escapeHtml(customerName)}</p>
                <p class="contentFont" style="font-size: 32px; text-align: right; margin-left: 100px; font-weight: 800;">${escapeHtml(customerInfoText)}</p>
            </div>
        </div>

        <hr style="border-top: 1px dashed #000;">

        <!-- Footer & Contact -->
        <div class="footer">
            <p class="contentFont" style="font-weight: 900; font-size: 32px;">Thank you for shopping! Please visit again.</p>
            <p class="contentFont" style="font-size: 32px; font-weight: 800;">If you have any questions, contact us here:</p>
            <p class="contentFont" style="font-size: 32px; font-weight: 800;">${storeEmail} | ${storePhone}</p>
            ${barcodeSvgHtml}
        </div>

        <hr style="border-top: 1px dashed #000; margin-top: 6px;">

        <div class="flexStyle" style="justify-content: center; align-items: center;">
            <p class="contentFont">Powered by</p>
            <p class="contentFont" style="font-weight: 900; margin-left: 10px;">ZenBasket</p>
        </div>
    </div>
</body>

</html>`;
}